import React, { useState, useEffect } from 'react';
import api from '../../api/api';
import { useTheme } from '../../context/ThemeContext';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';

interface MenuPerformanceProps {
  vendorId: number;
  startDate: string;
  endDate: string;
}

interface MenuItem {
  itemId: number;
  itemName: string;
  categoryName: string;
  quantitySold: number;
  totalRevenue: number;
}

interface CategoryPerformance {
  categoryName: string;
  totalRevenue: number;
  itemsSold: number;
}

interface MenuPerformanceData {
  topSellingItems: MenuItem[];
  topRevenueItems: MenuItem[];
  lowSellingItems: MenuItem[];
  categoryPerformance: CategoryPerformance[];
  totalItemsSold: number;
  uniqueItemsOrdered: number;
}

type SortBy = 'revenue' | 'quantity';

const MenuPerformance: React.FC<MenuPerformanceProps> = ({ vendorId, startDate, endDate }) => {
  const { theme } = useTheme();
  const [data, setData] = useState<MenuPerformanceData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortBy>('revenue');
  const [expandedSection, setExpandedSection] = useState<string | null>('topSelling');

  useEffect(() => {
    fetchMenuPerformance();
  }, [vendorId, startDate, endDate, sortBy]);

  const fetchMenuPerformance = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        startDate,
        endDate,
        sortBy,
      });

      const response = await api.get<MenuPerformanceData>(
        `/api/vendors/${vendorId}/reports/menu-performance?${params.toString()}`
      );

      setData(response.data);
    } catch (err: unknown) {
      console.error('Failed to fetch menu performance:', err);
      const error = err as { response?: { data?: { message?: string } } };
      setError(error.response?.data?.message || 'Failed to load menu performance data');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const renderTable = (items: MenuItem[], title: string, sectionKey: string) => {
    const isExpanded = expandedSection === sectionKey;

    return (
      <div
        className="glassmorphism-card"
        style={{
          background: theme.components.card.background,
          border: `1px solid ${theme.components.card.border}`,
          borderRadius: '16px',
          padding: '1.5rem',
          marginBottom: '1.5rem',
          boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.1)',
        }}
      >
        <button
          onClick={() => toggleSection(sectionKey)}
          className="w-full flex items-center justify-between mb-4"
          style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
        >
          <h3
            style={{
              fontSize: '1.25rem',
              fontWeight: '600',
              color: theme.text,
              margin: 0,
            }}
          >
            {title}
          </h3>
          {isExpanded ? (
            <ChevronUpIcon style={{ width: '20px', height: '20px', color: theme.text }} />
          ) : (
            <ChevronDownIcon style={{ width: '20px', height: '20px', color: theme.text }} />
          )}
        </button>

        {isExpanded && (
          <div style={{ overflowX: 'auto' }}>
            {items.length === 0 ? (
              <p style={{ color: theme.gray[600], textAlign: 'center', padding: '2rem 0' }}>
                No data available
              </p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr
                    style={{
                      borderBottom: `2px solid ${theme.components.card.border}`,
                    }}
                  >
                    <th
                      style={{
                        textAlign: 'left',
                        padding: '0.75rem 1rem',
                        color: theme.gray[600],
                        fontWeight: '600',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                      }}
                    >
                      Item Name
                    </th>
                    <th
                      style={{
                        textAlign: 'left',
                        padding: '0.75rem 1rem',
                        color: theme.gray[600],
                        fontWeight: '600',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                      }}
                    >
                      Category
                    </th>
                    <th
                      style={{
                        textAlign: 'right',
                        padding: '0.75rem 1rem',
                        color: theme.gray[600],
                        fontWeight: '600',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                      }}
                    >
                      Qty Sold
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => (
                    <tr
                      key={`${item.itemId}-${index}`}
                      style={{
                        borderBottom: `1px solid ${theme.components.card.border}`,
                        transition: 'background-color 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = theme.components.card.hover;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <td
                        style={{
                          padding: '1rem',
                          color: theme.text,
                          fontWeight: '500',
                        }}
                      >
                        {item.itemName}
                      </td>
                      <td
                        style={{
                          padding: '1rem',
                          color: theme.gray[600],
                        }}
                      >
                        {item.categoryName}
                      </td>
                      <td
                        style={{
                          padding: '1rem',
                          color: theme.text,
                          textAlign: 'right',
                          fontWeight: '500',
                        }}
                      >
                        {item.quantitySold}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderCategoryPerformance = () => {
    const isExpanded = expandedSection === 'category';

    return (
      <div
        className="glassmorphism-card"
        style={{
          background: theme.components.card.background,
          border: `1px solid ${theme.components.card.border}`,
          borderRadius: '16px',
          padding: '1.5rem',
          marginBottom: '1.5rem',
          boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.1)',
        }}
      >
        <button
          onClick={() => toggleSection('category')}
          className="w-full flex items-center justify-between mb-4"
          style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
        >
          <h3
            style={{
              fontSize: '1.25rem',
              fontWeight: '600',
              color: theme.text,
              margin: 0,
            }}
          >
            Category Performance
          </h3>
          {isExpanded ? (
            <ChevronUpIcon style={{ width: '20px', height: '20px', color: theme.text }} />
          ) : (
            <ChevronDownIcon style={{ width: '20px', height: '20px', color: theme.text }} />
          )}
        </button>

        {isExpanded && data && (
          <div style={{ overflowX: 'auto' }}>
            {data.categoryPerformance.length === 0 ? (
              <p style={{ color: theme.gray[600], textAlign: 'center', padding: '2rem 0' }}>
                No data available
              </p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr
                    style={{
                      borderBottom: `2px solid ${theme.components.card.border}`,
                    }}
                  >
                    <th
                      style={{
                        textAlign: 'left',
                        padding: '0.75rem 1rem',
                        color: theme.gray[600],
                        fontWeight: '600',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                      }}
                    >
                      Category
                    </th>
                    <th
                      style={{
                        textAlign: 'right',
                        padding: '0.75rem 1rem',
                        color: theme.gray[600],
                        fontWeight: '600',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                      }}
                    >
                      Items Sold
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.categoryPerformance.map((category, index) => (
                    <tr
                      key={`${category.categoryName}-${index}`}
                      style={{
                        borderBottom: `1px solid ${theme.components.card.border}`,
                        transition: 'background-color 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = theme.components.card.hover;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <td
                        style={{
                          padding: '1rem',
                          color: theme.text,
                          fontWeight: '500',
                        }}
                      >
                        {category.categoryName}
                      </td>
                      <td
                        style={{
                          padding: '1rem',
                          color: theme.text,
                          textAlign: 'right',
                          fontWeight: '500',
                        }}
                      >
                        {category.itemsSold}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '3rem',
          color: theme.text,
        }}
      >
        <div
          style={{
            width: '40px',
            height: '40px',
            border: `3px solid ${theme.gray[300]}`,
            borderTop: `3px solid ${theme.primary}`,
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }}
        />
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          background: theme.danger,
          color: 'var(--text-inverse)',
          padding: '1rem',
          borderRadius: '8px',
          margin: '1rem 0',
        }}
      >
        {error}
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <div style={{ padding: '1rem 0' }}>
      {/* Header with Sort Dropdown */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          flexWrap: 'wrap',
          gap: '1rem',
        }}
      >
        <h2
          style={{
            fontSize: '1.75rem',
            fontWeight: '700',
            color: theme.text,
            margin: 0,
          }}
        >
          Menu Performance
        </h2>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <label
            htmlFor="sortBy"
            style={{
              color: theme.gray[600],
              fontSize: '0.875rem',
              fontWeight: '500',
            }}
          >
            Sort by:
          </label>
          <select
            id="sortBy"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortBy)}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              border: `1px solid ${theme.components.input.border}`,
              background: theme.components.input.background,
              color: theme.text,
              fontSize: '0.875rem',
              fontWeight: '500',
              cursor: 'pointer',
              outline: 'none',
            }}
          >
            <option value="revenue">Revenue</option>
            <option value="quantity">Quantity</option>
          </select>
        </div>
      </div>

      {/* Overall Metrics */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '1rem',
          marginBottom: '1.5rem',
        }}
      >
        <div
          className="glassmorphism-card"
          style={{
            background: theme.components.card.background,
            border: `1px solid ${theme.components.card.border}`,
            borderRadius: '12px',
            padding: '1.5rem',
            boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.1)',
          }}
        >
          <p
            style={{
              color: theme.gray[600],
              fontSize: '0.875rem',
              fontWeight: '500',
              margin: '0 0 0.5rem 0',
              textTransform: 'uppercase',
            }}
          >
            Total Items Sold
          </p>
          <p
            style={{
              color: theme.text,
              fontSize: '2rem',
              fontWeight: '700',
              margin: 0,
            }}
          >
            {data.totalItemsSold.toLocaleString()}
          </p>
        </div>

        <div
          className="glassmorphism-card"
          style={{
            background: theme.components.card.background,
            border: `1px solid ${theme.components.card.border}`,
            borderRadius: '12px',
            padding: '1.5rem',
            boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.1)',
          }}
        >
          <p
            style={{
              color: theme.gray[600],
              fontSize: '0.875rem',
              fontWeight: '500',
              margin: '0 0 0.5rem 0',
              textTransform: 'uppercase',
            }}
          >
            Unique Items Ordered
          </p>
          <p
            style={{
              color: theme.text,
              fontSize: '2rem',
              fontWeight: '700',
              margin: 0,
            }}
          >
            {data.uniqueItemsOrdered}
          </p>
        </div>
      </div>

      {/* Performance Tables */}
      {renderTable(data.topSellingItems, 'Top 10 Selling Items', 'topSelling')}
      {renderTable(data.topRevenueItems, 'Top 10 Revenue Items', 'topRevenue')}
      {renderTable(data.lowSellingItems, 'Low Selling Items', 'lowSelling')}
      {renderCategoryPerformance()}
    </div>
  );
};

export default MenuPerformance;
